import { useSyncExternalStore } from 'react';
import { flushAppInsights, trackEvent } from '@/lib/applicationInsights';
import { isStandaloneDisplay } from '@/lib/displayMode';

export const INSTALL_PROMPT_DISMISSED_KEY = 'crimson:installPromptDismissed';

export const APP_INSTALL_EVENT = {
  offered: 'AppInstall.Offered',
  dismissed: 'AppInstall.Dismissed',
  clicked: 'AppInstall.Clicked',
  outcome: 'AppInstall.Outcome',
  installed: 'AppInstall.Installed',
} as const;

export function trackAppInstall(
  name: (typeof APP_INSTALL_EVENT)[keyof typeof APP_INSTALL_EVENT],
  properties?: Record<string, string>,
): void {
  trackEvent(name, properties);
}

export interface AppInstallPromptEvent extends Event {
  prompt: () => Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let offer: AppInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function setOffer(next: AppInstallPromptEvent | null): void {
  offer = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The browser's pending install offer, or null once used, dismissed or installed. */
export function useInstallOffer(): AppInstallPromptEvent | null {
  return useSyncExternalStore(subscribe, () => offer, () => null);
}

export function isInstallPromptDismissed(): boolean {
  try {
    return localStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Hide the current offer without remembering, so a later visit can ask again. */
export function withdrawInstallOffer(): void {
  setOffer(null);
}

/** Never offer again: remembers the choice and withdraws the current offer. */
export function dismissInstallPrompt(): void {
  withdrawInstallOffer();
  try {
    localStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, '1');
  } catch {
    // Without storage the prompt can return on a later visit.
  }
}

/**
 * Captures Chromium's install offer for the page. Chrome fires
 * `beforeinstallprompt` once per load, possibly before React mounts (the app
 * renders nothing until env config loads), so call this before rendering.
 */
export function listenForInstallOffers(): () => void {
  if (isStandaloneDisplay() || isInstallPromptDismissed()) return () => {};

  const onBeforeInstall = (event: Event) => {
    event.preventDefault();
    setOffer(event as AppInstallPromptEvent);
  };

  const onInstalled = () => {
    withdrawInstallOffer();
    trackAppInstall(APP_INSTALL_EVENT.installed);
    flushAppInsights();
  };

  window.addEventListener('beforeinstallprompt', onBeforeInstall);
  window.addEventListener('appinstalled', onInstalled);
  return () => {
    window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    window.removeEventListener('appinstalled', onInstalled);
    setOffer(null);
  };
}
